<script setup lang="ts">
import { isIotDeviceWithoutLicense } from '@/views/qaEquipment/utils';
import { QaEquipmentRtSystemDto } from '@/api/generator';
import { useLicenseStore } from '@/stores/licenseStore';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import Tooltip from '@/components/Tooltip/Tooltip.vue';

const { variant = 'default', device } = defineProps<{
  variant?: 'default' | 'icon-only';
  device: QaEquipmentRtSystemDto;
}>();

const licenseStore = useLicenseStore();
const { expiredDeviceLicenses, allDeviceLicensesMasterDeviceIds } = storeToRefs(licenseStore);

const showExpiredAlert = computed(
  () => (device: QaEquipmentRtSystemDto) =>
    device.masterDeviceId && expiredDeviceLicenses.value.has(device.masterDeviceId),
);

const showMissingAlert = computed(
  () => (device: QaEquipmentRtSystemDto) =>
    isIotDeviceWithoutLicense(device, allDeviceLicensesMasterDeviceIds.value),
);

const licenseStatus = computed(() => {
  if (showExpiredAlert.value(device)) {
    return {
      hasAlert: true as const,
      message: 'TheDevicesLicenseHasExpired' as const,
    };
  }
  if (showMissingAlert.value(device)) {
    return {
      hasAlert: true as const,
      message: 'TheDevicesLicenseIsMissing' as const,
    };
  }
  return {
    hasAlert: false as const,
  };
});
</script>

<template>
  <template v-if="licenseStatus.hasAlert">
    <!-- Default variant: full alert -->
    <v-alert
      v-if="variant === 'default'"
      type="error"
      variant="tonal"
      density="compact"
      class="text-body-1 mt-2 license-alert"
    >
      <template #text>
        {{ $t(licenseStatus.message) }}
      </template>
    </v-alert>

    <!-- Icon-only variant -->
    <Tooltip v-else position="top">
      <template #target="{ props: targetProps }">
        <v-alert
          v-bind="targetProps"
          type="error"
          variant="tonal"
          class="license-icon d-inline-flex"
          density="compact"
        />
      </template>
      <template #tooltip>
        {{ $t(licenseStatus.message) }}
      </template>
    </Tooltip>
  </template>
</template>

<style scoped lang="scss">
.license-icon {
  min-width: 24px;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  padding: 0 !important;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  :deep(.v-alert__content) {
    display: none;
  }

  :deep(.v-alert__prepend) {
    align-self: center;
    margin: 0 !important;
  }

  :deep(.v-icon) {
    font-size: 16px;
    opacity: 1;
  }
}

.license-alert {
  width: fit-content;
  max-width: 100%;
  word-break: keep-all;
  overflow-wrap: normal;
  overflow: visible;
  hyphens: none;
  white-space: normal;
  margin-left: 0;
  margin-right: 0;

  :deep(.v-alert__content) {
    word-break: keep-all;
    overflow-wrap: normal;
    overflow: visible;
    hyphens: none;
    white-space: normal;
    line-height: 1.3;
  }
}

:deep(.v-icon) {
  flex-shrink: 0;
}
</style>
